import React, { useEffect, useState } from "react";
import { endpoints } from "../lib/api";
import { useAuth } from "../lib/auth";
import AIInsights from "../components/AIInsights";
import CheckinChart from "../components/CheckinChart";
import GymHealthCard from "../components/GymHealthCard";
import TodayFocusCard from "../components/TodayFocusCard";
import RevenueSnapshot from "../components/RevenueSnapshot";
import RecentActivityFeed from "../components/RecentActivityFeed";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
}

/**
 * Dashboard - workflow-first layout per design spec v5.
 *
 * Sections:
 *   1. Today's Focus  (hero - the only thing they *must* see)
 *   2. Gym Health     (the 87/100 health card)
 *   3. Revenue        (4-card money snapshot, much smaller than before)
 *   4. Recent Activity feed
 *
 * Plus: 7-day attendance chart and AI insights kept from previous version.
 */
export default function Dashboard() {
  const { isDemo } = useAuth();
  const [dash, setDash] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [d, t, m, l] = await Promise.all([
          endpoints.dashboard().catch(() => ({ data: null })),
          endpoints.listTasks().catch(() => ({ data: [] })),
          endpoints.listMembers().catch(() => ({ data: [] })),
          endpoints.listLeads().catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setDash(d.data || {});
        setTasks(t.data || []);
        setMembers(m.data || []);
        setLeads(l.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isDemo]);

  const stats = dash?.stats || {};

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10 max-w-6xl mx-auto">
      <header className="mb-8 lg:mb-10">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70 mb-2">Home</p>
        <h1
          className="font-display text-3xl md:text-4xl lg:text-[44px] font-bold tracking-tight text-foreground leading-[1.05]"
          data-testid="dashboard-title"
        >
          Good {greeting()}. Here's your day.
        </h1>
      </header>

      {/* AI Insights (above the fold but compact) */}
      <div className="mb-6">
        <AIInsights />
      </div>

      {/* HERO: Today's Focus */}
      <div className="mb-6">
        <TodayFocusCard tasks={tasks} members={members} loading={loading} />
      </div>

      {/* Gym Health Score */}
      <div className="mb-8">
        <GymHealthCard stats={stats} loading={loading} />
      </div>

      {/* Revenue snapshot */}
      <div className="mb-8">
        <RevenueSnapshot stats={stats} loading={loading} />
      </div>

      {/* Two-column on desktop: activity + check-in chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
        <RecentActivityFeed leads={leads} members={members} loading={loading} />
        <CheckinChart />
      </div>
    </div>
  );
}
